export function parseCurrencyToInt(str) {
  if (str === null || str === undefined || str === "") return null;
  const digits = String(str).replaceAll(/\D/g, "");
  if (digits === "") return null;
  return Number.parseInt(digits, 10);
}

/**
 * The company values the Add Submission form showed when it opened (S2CWB-333).
 *
 * Sent back as `insured.prefill` so the server can tell what the broker changed: a
 * company field is overwritten only when the submitted value differs from the one the
 * form showed, so a form opened before another session saved a newer value cannot put
 * its old one back.
 */
export function buildPrefillBaseline(form) {
  return {
    companyName: form.companyName || "",
    yearEstablished: form.yearEstablished || "",
    address: form.address || "",
    state: form.state || "",
    website: form.websiteUrl || "",
    revenueTtm: parseCurrencyToInt(form.revenue),
    employeeCount: parseEmployeeCount(form.employeeCount),
    naics: {
      primaryCode: form.primaryNaicsCode || "",
      primaryDescription: form.primaryNaicsDescription || "",
    },
  };
}

export function parseNaicsQuery(str) {
  if (!str?.trim()) return null;

  // Format 3: "Name (Code: 327910)" — backend csgData style
  const codeInParens = str.match(/\(Code:\s*(\d+)\)/i);
  if (codeInParens) {
    const code = codeInParens[1];
    const start = codeInParens.index;
    const description = `${str.slice(0, start)}${str.slice(start + codeInParens[0].length)}`.trim();
    return { code, description };
  }

  // Format 1 & 2: "327910 — Name" (em-dash) or "327910 - Name" (hyphen)
  const emDash = " — ";
  const hyphen = " - ";
  const sep = str.includes(emDash) ? emDash : hyphen;
  const idx = str.indexOf(sep);
  if (idx === -1) {
    return { code: str.trim(), description: "" };
  }
  return {
    code: str.slice(0, idx).trim(),
    description: str.slice(idx + sep.length).trim(),
  };
}

/**
 * Employee count as sent to the API: a number (0 included), or null when left
 * blank so the API refuses it instead of a silent 0 (S2CWB-333).
 */
export function parseEmployeeCount(val) {
  if (val === null || val === undefined || String(val).trim() === "") return null;
  const parsed = Number.parseInt(val, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * A security answer as sent to the API: "Yes", "No", or null when the broker
 * did not answer. An unanswered question is never sent as "No" (S2CWB-333).
 */
export function answerToYesNo(val) {
  if (val === true || val === "Yes") return "Yes";
  if (val === false || val === "No") return "No";
  return null;
}

export function buildOptionPayload(optionForm, optionNumber) {
  const coverageLimit = parseCurrencyToInt(optionForm.limit);
  const deductible = parseCurrencyToInt(optionForm.retention);
  const targetPremium = parseCurrencyToInt(optionForm.targetPremium);

  const option = {
    optionNumber,
    effectiveDate: optionForm.effectiveDate || "",
    endDate: optionForm.endDate || "",
    priorActsDate: optionForm.priorActsDate || "",
    // A blank stays null so the API refuses it (S2CWB-333) instead of a silent 0.
    coverageLimit,
    deductible,
    coverages: optionForm.coverages ? [...optionForm.coverages] : [],
  };

  if (targetPremium !== null) {
    option.targetPremium = targetPremium;
  }

  const waitingPeriod = optionForm.waitingPeriod;
  if (waitingPeriod !== undefined && waitingPeriod !== null && waitingPeriod !== "") {
    option.waitingPeriodHours = Number.parseInt(waitingPeriod, 10);
  }

  return option;
}

/**
 * Normalize wizard/API carrier selections to POST body shape:
 * `{ carrierOrganizationId: string, carrierName?: string }[]`.
 * Dedupes by `carrierOrganizationId` (first `carrierName` wins).
 *
 * @param {unknown[]} raw - `form.carriers` (objects and/or legacy strings)
 * @param {Record<string, string>} [nameToIdLookup] - optional display name → org id for legacy forms
 */
function resolveCarrierFromString(entry, lookup) {
  const s = String(entry).trim();
  if (!s) return null;
  const resolved = lookup[s];
  if (resolved == null || String(resolved).trim() === "") return null;
  const carrierOrganizationId = String(resolved).trim();
  const carrierName = s === carrierOrganizationId ? undefined : s;
  return { carrierOrganizationId, carrierName };
}

function resolveCarrierFromObject(entry) {
  const carrierOrganizationId = String(entry.uwb_carrier_id ?? entry.carrierOrganizationId ?? "").trim();
  const label = entry.carrierName ?? entry.name;
  const carrierName = label != null && String(label).trim() !== "" ? String(label).trim() : undefined;
  return { carrierOrganizationId, carrierName };
}

function resolveCarrierEntry(entry, lookup) {
  if (entry == null) return null;
  if (typeof entry === "string") return resolveCarrierFromString(entry, lookup);
  if (typeof entry === "object") return resolveCarrierFromObject(entry);
  return null;
}

export function normalizeCarriersForPayload(raw, nameToIdLookup) {
  const lookup = nameToIdLookup && typeof nameToIdLookup === "object" ? nameToIdLookup : {};
  const list = Array.isArray(raw) ? raw : [];
  const firstIds = [];
  const byId = new Map();

  for (const entry of list) {
    const resolved = resolveCarrierEntry(entry, lookup);
    if (!resolved?.carrierOrganizationId) continue;

    const { carrierOrganizationId, carrierName } = resolved;
    if (byId.has(carrierOrganizationId)) continue;

    const row = { carrierOrganizationId };
    if (carrierName && carrierName !== carrierOrganizationId) {
      row.carrierName = carrierName;
    }
    byId.set(carrierOrganizationId, row);
    firstIds.push(carrierOrganizationId);
  }

  return firstIds.map((id) => byId.get(id));
}

/**
 * Normalize uploaded documents to the batch submit body shape.
 * Only successfully uploaded S3 refs are included.
 *
 * @param {unknown[]} raw - `form.documents` from the wizard state
 */
export function normalizeDocumentsForPayload(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .filter((d) => d?.handle && (d.status === "uploaded" || !d.status))
    .map((d) => ({
      handle: d.handle,
      fileName: String(d.fileName || "").trim(),
      fileType: String(d.fileType || "application/pdf").trim() || "application/pdf",
    }))
    .filter((d) => d.fileName);
}

export function expandSubmissionFormToBatchForms(form) {
  const optionals = form.optionals || [];
  if (!optionals.length) {
    return [form];
  }

  const slices = [{ ...form, optionals: [] }];

  for (const opt of optionals) {
    slices.push({
      ...form,
      limit: opt.limit,
      retention: opt.retention,
      waitingPeriod: opt.waitingPeriod,
      priorActsDate: opt.priorActsDate,
      targetPremium: opt.targetPremium,
      coverages: Array.isArray(opt.coverages) ? [...opt.coverages] : [],
      carriers: Array.isArray(opt.carriers) ? [...opt.carriers] : [],
      effectiveDate: opt.effectiveDate || form.effectiveDate,
      endDate: opt.endDate || form.endDate,
      optionals: [],
    });
  }

  return slices;
}

export function buildSubmissionPayload(form, carriersMap) {
  const nameToIdLookup = carriersMap && typeof carriersMap === "object" ? carriersMap : {};

  const primaryNaics = {
    code: form.primaryNaicsCode || "",
    description: form.primaryNaicsDescription || "",
  };
  const secondaryNaics = form.secondaryNaicsCode
    ? { code: form.secondaryNaicsCode, description: form.secondaryNaicsDescription || "" }
    : { code: "", description: "" };

  const revenue = parseCurrencyToInt(form.revenue);

  const insured = {
    companyName: form.companyName || "",
    yearEstablished: form.yearEstablished || "",
    address: form.address || "",
    // Optional uppercase 2-char US abbreviation alongside the free-text address;
    // blank means "not stated". It is what the jurisdiction advisory reads as the
    // insured's jurisdiction (S2CWB-237), so it is sent as its own field rather
    // than left buried in the address line.
    state: form.state || "",
    website: form.websiteUrl || "",
    revenueTTM: revenue,
    employeeCount: parseEmployeeCount(form.employeeCount),
    contact: {
      name: form.insuredContact || "",
      email: form.insuredEmail || "",
    },
  };

  insured.primaryNaics = primaryNaics;
  insured.secondaryNaics = secondaryNaics;
  if (form.prefill) {
    insured.prefill = form.prefill;
  }

  const optionals = form.optionals || [];
  const sharedDates = { effectiveDate: form.effectiveDate, endDate: form.endDate };
  const options = [
    buildOptionPayload(form, 1),
    ...optionals.map((opt, i) => buildOptionPayload({ ...sharedDates, ...opt }, i + 2)),
  ];

  const carriers = normalizeCarriersForPayload(form.carriers, nameToIdLookup);

  const documents = normalizeDocumentsForPayload(form.documents);

  const payload = {
    broker: {
      name: form.brokerName || "",
      email: (form.brokerContact || "").trim(),
      brokerage: form.brokerageName || "",
    },
    insured,
    options,
    carriers,
  };

  // The security answers are always sent, and documents go alongside them
  // (S2CWB-333). With documents attached an answer may be left unanswered (null).
  payload.securityProfile = {
    mfaEnforcedForEmailAndRemoteAccess: answerToYesNo(form.mfaEnforced),
    offlineOrImmutableBackupsInPlace: answerToYesNo(form.offlineBackups),
    itManagedByExternalThirdParty: answerToYesNo(form.itManagedExternal),
    dualAuthorizationRequiredForFundTransfers: answerToYesNo(form.dualAuthFunds),
    cyberIncidentsInLast3Years: answerToYesNo(form.cyberIncidents),
  };
  if (documents.length) {
    payload.documents = documents;
  }

  // The API schema expects a top-level `insuranceType`. A nested `submission`
  // wrapper is not in the schema and is ignored (server then defaults to "cyber").
  if (form.insuranceType) {
    payload.insuranceType = form.insuranceType;
  }

  // Carrier notes — the API expects `brokerMessage`; a `message` field is ignored.
  if (form.message?.trim()) {
    payload.brokerMessage = form.message.trim();
  }

  return payload;
}
